import { createRef, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker, renderTimeViewClock } from "@mui/x-date-pickers";
import { useSearchParams } from "react-router-dom";

import PageLayout from "../components/layout/PageLayout";
import OperationsLogs from "components/OperationsPage/OperationsLogs";
import PaginationBar from "components/utils/PaginationBar";
import DateTimePickerActionBar from "components/utils/DatePickerActionBar";
import Alert from "components/utils/Alert";
import { DATE_VIEWS, MovementTypes, Roles } from "constants/index";
import {
  MOVEMENTS_EXPORT_URL,
  MOVEMENTS_FILTER_OPTIONS_URL,
  MOVEMENTS_URL,
} from "constants/urls";
import { queryBuilder, urlParamsToObject } from "helpers/query";
import useCrud from "hooks/useCrud";
import { useSession } from "providers/SessionProvider";
import {
  Article,
  Movement,
  MovementExportRow,
  MovementsExportResponse,
} from "types";
import dayjs from "dayjs";
import { FaFileDownload } from "react-icons/fa";
import { exportMovementsToExcel } from "helpers/exportToExcel";
import { axiosGet } from "api";
import { useReactToPrint } from "react-to-print";
import { PrintLayout } from "components/Prints/PrintLayout";
import OperationsListPrint, {
  OperationsListPrintHeader,
} from "components/Prints/Operations/OperationsListPrint";

type FiltersState = {
  id?: string;
  clientId?: number;
  associatedClient?: string;
  articleId?: number;
  articleCode?: string;
  storeId?: number;
  fromDate: Date | null;
  toDate: Date | null;
  movementTypeIds: number[];
  lote?: string;
  oc?: string;
  amountPerContainer?: number;
  articleDescription?: string;
  fechaVencimiento: Date | null;
  fechaIngreso: Date | null;
  includeDrafts: boolean;
  includeAdjustments?: boolean;
  page: number;
};

type MovementFilterOptionsResponse = {
  amount_per_container: number[];
  lote: string[];
  oc: string[];
  fecha_vencimiento: string[];
  fecha_ingreso: string[];
};

const MOVEMENT_TYPE_OPTIONS = [
  { value: MovementTypes.Income, label: "Ingreso" },
  { value: MovementTypes.Discharge, label: "Egreso" },
  { value: MovementTypes.Additional, label: "Adicionales" },
  { value: MovementTypes.Preparation, label: "Preparación" },
  { value: MovementTypes.Transfer, label: "Transferencias" },
  { value: MovementTypes.WorkOrder, label: "Orden de trabajo" },
];

const CLIENT_MOVEMENT_TYPE_IDS = [
  MovementTypes.Income,
  MovementTypes.Discharge,
  MovementTypes.Preparation,
];

export default function OperationsPage() {
  const { clients, associatedClients, articles, stores, userData } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = urlParamsToObject(searchParams);
  const isClientUser = userData?.role === Roles.CLIENT;

  const [filters, setFilters] = useState<FiltersState>(() => ({
    id: params.id || undefined,
    clientId: params.client ? parseInt(params.client) : undefined,
    associatedClient: params.as_client || undefined,
    articleId: params.article ? parseInt(params.article) : undefined,
    articleCode: params.articleCode || undefined,
    storeId: params.warehouse ? parseInt(params.warehouse) : undefined,
    fromDate: params.from ? new Date(params.from) : null,
    toDate: params.to ? new Date(params.to) : null,
    movementTypeIds: params.mov_type
      ? params.mov_type.split(",").map(Number)
      : [],
    lote: params.lote || undefined,
    oc: params.oc || undefined,
    amountPerContainer:
      params.amount_per_container != null && params.amount_per_container !== ""
        ? Number(params.amount_per_container)
        : undefined,
    articleDescription: params.articleDescription || undefined,
    fechaVencimiento: params.fv ? new Date(params.fv) : null,
    fechaIngreso: params.fi ? new Date(params.fi) : null,
    includeDrafts: !!params.includeDrafts,
    includeAdjustments: !!params.includeAdjustments,
    page: parseInt(params.page || "1"),
  }));
  const [idText, setIdText] = useState(() => params.id || "");

  useEffect(() => {
    if (!isClientUser) return;

    setFilters((previous) => {
      const allowedSelectedTypes = previous.movementTypeIds.filter((id) =>
        CLIENT_MOVEMENT_TYPE_IDS.includes(id),
      );
      const movementTypeIds =
        allowedSelectedTypes.length > 0
          ? allowedSelectedTypes
          : [...CLIENT_MOVEMENT_TYPE_IDS];
      const clientId = userData?.client_id ?? undefined;
      const isAlreadyScoped =
        previous.clientId === clientId &&
        previous.includeDrafts === false &&
        previous.includeAdjustments === false &&
        previous.movementTypeIds.length === movementTypeIds.length &&
        previous.movementTypeIds.every(
          (id, index) => id === movementTypeIds[index],
        );

      if (isAlreadyScoped) return previous;

      return {
        ...previous,
        clientId,
        movementTypeIds,
        includeDrafts: false,
        includeAdjustments: false,
        page: 1,
      };
    });
  }, [isClientUser, userData?.client_id]);

  const {
    data: movements,
    count: totalCount,
    fetchData: getMovements,
    loading: loadingMovements,
  } = useCrud<Movement>(MOVEMENTS_URL, { disableAutoFetch: true });

  const { fetchData: getMovementsExport, loading: loadingAllMovements } =
    useCrud<MovementsExportResponse>(MOVEMENTS_EXPORT_URL, {
      disableAutoFetch: true,
    });
  const [filterOptions, setFilterOptions] =
    useState<MovementFilterOptionsResponse>({
      amount_per_container: [],
      lote: [],
      oc: [],
      fecha_vencimiento: [],
      fecha_ingreso: [],
    });
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);
  const [printMovements, setPrintMovements] = useState<MovementExportRow[]>([]);
  const [printPending, setPrintPending] = useState(false);

  const isClientSelected =
    filters.clientId !== undefined && filters.clientId !== null; // 0 incluido
  const printRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
  });

  const uniqueArticles = useMemo(
    () => Array.from(new Map(articles.map((a) => [a.code, a])).values()),
    [articles],
  );

  const updateFilters = (updates: Partial<FiltersState>) =>
    setFilters((prev) => ({ ...prev, ...updates, page: 1 }));

  const setPage = (page: number) => setFilters((prev) => ({ ...prev, page }));

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === filters.clientId) || null,
    [filters.clientId, clients],
  );

  const isAllClients = filters.clientId === 0;

  const clientAssociatedClients = useMemo(() => {
    if (filters.clientId == null) return [];
    if (isAllClients) return associatedClients;
    return associatedClients.filter((ac) => ac.client?.id === filters.clientId);
  }, [associatedClients, filters.clientId, isAllClients]);

  const clientOptions = useMemo(
    () => [
      { id: 0, label: "Todos" },
      ...clients.filter((c) => c.parent_client_id == null).map((c) => ({
        id: c.id,
        label: `${c.code} - ${c.razon_social}`,
      })),
    ],
    [clients],
  );

  const selectedClientOption =
    clientOptions.find((c) => c.id === filters.clientId) ?? null;

  const associatedClientOptions = useMemo(
    () =>
      clientAssociatedClients.map((ac) => ({
        id: ac.id,
        label: ac.razon_social,
      })),
    [clientAssociatedClients],
  );

  const selectedAssociated =
    associatedClientOptions.find(
      (ac) => ac.label === filters.associatedClient,
    ) ?? null;

  const storeOptions = useMemo(
    () => [
      { id: 0, label: "Todos" },
      ...stores.map((s) => ({ id: s.id, label: s.description })),
    ],
    [stores],
  );

  const selectedStore =
    storeOptions.find((s) => s.id === (filters.storeId ?? 0)) ?? null;

  const amountPerContainerOptions = useMemo(
    () =>
      filterOptions.amount_per_container.map((value) => ({
        id: `apc-${value}`,
        label: String(value),
        value,
      })),
    [filterOptions.amount_per_container],
  );

  const loteOptions = useMemo(
    () =>
      filterOptions.lote.map((value, index) => ({
        id: `${value}-${index}`,
        label: value,
      })),
    [filterOptions.lote],
  );

  const ocOptions = useMemo(
    () =>
      filterOptions.oc.map((value, index) => ({
        id: `${value}-${index}`,
        label: value,
      })),
    [filterOptions.oc],
  );

  const fechaVencimientoOptions = useMemo(
    () =>
      filterOptions.fecha_vencimiento.map((value, index) => ({
        id: `${value}-${index}`,
        label: dayjs(value).format("DD/MM/YYYY"),
        value,
      })),
    [filterOptions.fecha_vencimiento],
  );

  const fechaIngresoOptions = useMemo(
    () =>
      filterOptions.fecha_ingreso.map((value, index) => ({
        id: `${value}-${index}`,
        label: dayjs(value).format("DD/MM/YYYY"),
        value,
      })),
    [filterOptions.fecha_ingreso],
  );

  async function handleExportMovements(format: "excel" | "pdf") {
    try {
      const query = queryBuilder({
        ...filters,
        amount_per_container: filters.amountPerContainer,
        fv: filters.fechaVencimiento,
        fi: filters.fechaIngreso,
      });

      const response = await getMovementsExport(query, true, {}, false);
      const exportData = response?.data as MovementsExportResponse | undefined;
      const hasMovements = Boolean(exportData?.movements?.length);
      const hasAdditionalItems = Boolean(exportData?.additional_items?.length);
      const hasExportData =
        hasMovements || (format === "excel" && hasAdditionalItems);

      if (exportData && hasExportData) {
        const exportArgs = [
          exportData.movements,
          exportData.additional_items ?? [],
          filters.fromDate ? filters.fromDate.toISOString() : "",
          filters.toDate ? filters.toDate.toISOString() : "",
          selectedClient?.razon_social || "TODOS",
        ] as const;

        if (format === "pdf") {
          setPrintMovements(exportData.movements);
          setPrintPending(true);
        } else {
          exportMovementsToExcel(...exportArgs);
        }
      } else {
        console.warn("⚠️ No se encontraron movimientos para exportar");
      }
    } catch (error) {
      console.error("❌ Error al exportar movimientos:", error);
    }
  }

  useEffect(() => {
    if (!printPending || printMovements.length === 0) {
      return;
    }

    handlePrint();
    setPrintPending(false);
  }, [handlePrint, printMovements, printPending]);

  useEffect(() => {
    setIdText(filters.id ?? "");
  }, [filters.id]);

  useEffect(() => {
    if (filters.clientId === undefined || filters.clientId === null) {
      setFilterOptions({
        amount_per_container: [],
        lote: [],
        oc: [],
        fecha_vencimiento: [],
        fecha_ingreso: [],
      });
      return;
    }

    const fetchFilterOptions = async () => {
      setLoadingFilterOptions(true);

      try {
        const query = queryBuilder({
          id: filters.id,
          clientId: filters.clientId,
          associatedClient: filters.associatedClient,
          articleId: filters.articleId,
          articleCode: filters.articleCode,
          storeId: filters.storeId,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          movementTypeIds:
            filters.movementTypeIds.length > 0
              ? filters.movementTypeIds.join(",")
              : undefined,
          includeDrafts: filters.includeDrafts || undefined,
          includeAdjustments: filters.includeAdjustments || undefined,
          lote: filters.lote,
          oc: filters.oc,
          amount_per_container: filters.amountPerContainer,
          articleDescription: filters.articleDescription?.trim() || undefined,
          fv: filters.fechaVencimiento,
          fi: filters.fechaIngreso,
        });

        const response = await axiosGet(
          `${MOVEMENTS_FILTER_OPTIONS_URL}${query}`,
          true,
        );

        if (response.status === 200 && response.data) {
          setFilterOptions(response.data as MovementFilterOptionsResponse);
          return;
        }
      } finally {
        setLoadingFilterOptions(false);
      }

      setFilterOptions({
        amount_per_container: [],
        lote: [],
        oc: [],
        fecha_vencimiento: [],
        fecha_ingreso: [],
      });
    };

    void fetchFilterOptions();
  }, [
    filters.id,
    filters.clientId,
    filters.associatedClient,
    filters.articleId,
    filters.articleCode,
    filters.storeId,
    filters.fromDate,
    filters.toDate,
    filters.movementTypeIds,
    filters.includeDrafts,
    filters.includeAdjustments,
    filters.lote,
    filters.oc,
    filters.amountPerContainer,
    filters.articleDescription,
    filters.fechaVencimiento,
    filters.fechaIngreso,
  ]);

  useEffect(() => {
    if (filters.clientId === undefined || filters.clientId === null) return;

    const query = queryBuilder({
      ...filters,
      amount_per_container: filters.amountPerContainer,
      fv: filters.fechaVencimiento,
      fi: filters.fechaIngreso,
    });

    getMovements(query, true, {}, true);

    const nextParams: Record<string, string> = {};
    if (filters.id) nextParams.id = filters.id;
    if (filters.clientId !== undefined && filters.clientId !== null)
      nextParams.client = filters.clientId.toString();
    if (filters.associatedClient)
      nextParams.as_client = filters.associatedClient;
    if (filters.articleCode) nextParams.articleCode = filters.articleCode;
    else if (filters.articleId) nextParams.article = filters.articleId.toString();
    if (filters.storeId) nextParams.warehouse = filters.storeId.toString();
    if (filters.fromDate) nextParams.from = filters.fromDate.toISOString();
    if (filters.toDate) nextParams.to = filters.toDate.toISOString();
    if (filters.movementTypeIds.length > 0)
      nextParams.mov_type = filters.movementTypeIds.join(",");
    if (filters.includeDrafts) nextParams.includeDrafts = "1";
    if (filters.includeAdjustments) nextParams.includeAdjustments = "1";
    if (filters.amountPerContainer != null) {
      nextParams.amount_per_container = String(filters.amountPerContainer);
    }
    if (filters.articleDescription) {
      nextParams.articleDescription = filters.articleDescription;
    }
    if (filters.fechaVencimiento)
      nextParams.fv = filters.fechaVencimiento.toISOString();
    if (filters.fechaIngreso)
      nextParams.fi = filters.fechaIngreso.toISOString();

    nextParams.page = filters.page.toString();

    setSearchParams(nextParams);
  }, [filters]);

  return (
    <PageLayout
      pageTitle="Movimientos"
      pageDesc={
        isClientUser
          ? "Movimientos de ingresos, egresos y pedidos"
          : "Movimientos de ingresos, egresos y adicionales"
      }
    >
      <PrintLayout
        ref={printRef}
        headerInfo={
          <OperationsListPrintHeader
            fromDate={filters.fromDate ? filters.fromDate.toISOString() : ""}
            toDate={filters.toDate ? filters.toDate.toISOString() : ""}
            clientLabel={selectedClient?.razon_social || "TODOS"}
          />
        }
      >
        <OperationsListPrint
          movements={printMovements}
          fromDate={filters.fromDate ? filters.fromDate.toISOString() : ""}
          toDate={filters.toDate ? filters.toDate.toISOString() : ""}
          clientLabel={selectedClient?.razon_social || "TODOS"}
        />
      </PrintLayout>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          p: 2,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">Filtros</Typography>
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={2.4}>
            <TextField
              label="N° Movimiento"
              value={idText}
              disabled={loadingMovements}
              onChange={(e) => setIdText(e.target.value)}
              onBlur={() =>
                updateFilters({ id: idText.trim() ? idText.trim() : undefined })
              }
              fullWidth
              type="number"
            />
          </Grid>

          <Grid item xs={2.4}>
            <Autocomplete
              id="client"
              autoComplete
              disabled={loadingMovements || isClientUser}
              value={selectedClientOption}
              options={clientOptions}
              onChange={(_, val) =>
                updateFilters({
                  clientId: val?.id,
                  associatedClient: undefined,
                  articleId: undefined,
                  articleCode: undefined,
                  lote: "",
                  oc: "",
                  amountPerContainer: undefined,
                  fechaVencimiento: null,
                  fechaIngreso: null,
                })
              }
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} required label="Cliente" />
              )}
              fullWidth
            />
          </Grid>

          <Grid item xs={2.4}>
            <Autocomplete
              id="associated-client"
              value={selectedAssociated}
              options={associatedClientOptions}
              onChange={(_, val) =>
                updateFilters({ associatedClient: val?.label })
              }
              isOptionEqualToValue={(o, v) => o.label === v.label}
              renderInput={(params) => (
                <TextField {...params} label="Cliente Asociado" />
              )}
              fullWidth
              disabled={!isClientSelected || loadingMovements}
            />
          </Grid>

          <Grid item xs={2.4}>
            <DateTimePicker
              sx={{ width: "100%" }}
              value={filters.fromDate}
              onChange={(val) => updateFilters({ fromDate: val })}
              disableFuture
              label="Desde"
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              slots={{ actionBar: DateTimePickerActionBar }}
              views={DATE_VIEWS}
              disabled={loadingMovements}
            />
          </Grid>

          <Grid item xs={2.4}>
            <DateTimePicker
              sx={{ width: "100%" }}
              disableFuture
              label="Hasta"
              value={filters.toDate}
              disabled={loadingMovements}
              onChange={(val) => updateFilters({ toDate: val })}
              viewRenderers={{
                hours: renderTimeViewClock,
                minutes: renderTimeViewClock,
                seconds: renderTimeViewClock,
              }}
              slots={{ actionBar: DateTimePickerActionBar }}
              views={DATE_VIEWS}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Autocomplete
              id="warehouse"
              autoComplete
              value={selectedStore}
              options={storeOptions}
              onChange={(_, val) => {
                if (!val) return;
                if (val.id === 0) updateFilters({ storeId: undefined });
                else updateFilters({ storeId: val.id });
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              disabled={loadingMovements}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Almacen" />
              )}
            />
          </Grid>

          <Grid item md={2.4} sm={6} xs={12}>
            <Autocomplete
              id="article"
              options={uniqueArticles}
              getOptionLabel={(option: Article) => option.code ?? ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) =>
                options.filter((o) =>
                  (o.code ?? "")
                    .toLowerCase()
                    .includes(state.inputValue.toLowerCase()),
                )
              }
              value={
                uniqueArticles.find(
                  (a) =>
                    a.code === filters.articleCode ||
                    (!filters.articleCode && a.id === filters.articleId),
                ) || null
              }
              disabled={loadingMovements || !isClientSelected}
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  articleId: undefined,
                  articleCode: value?.code,
                  amountPerContainer: undefined,
                  lote: "",
                  oc: "",
                  fechaVencimiento: null,
                  fechaIngreso: null,
                }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Artículo" />
              )}
            />
          </Grid>

          <Grid item md={2.4} sm={6} xs={12}>
            <Autocomplete
              options={amountPerContainerOptions}
              value={
                amountPerContainerOptions.find(
                  (o) => Number(o.value) === Number(filters.amountPerContainer),
                ) || null
              }
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  amountPerContainer: value?.value ?? undefined,
                  lote: "",
                  oc: "",
                  fechaVencimiento: null,
                  fechaIngreso: null,
                }))
              }
              disabled={
                (!filters.articleCode && !filters.articleId) ||
                loadingMovements ||
                loadingFilterOptions
              }
              renderInput={(params) => (
                <TextField {...params} label="Cant. x Contenedor" />
              )}
            />
          </Grid>

          <Grid item md={2.4} sm={6} xs={12}>
            <Autocomplete
              options={loteOptions}
              value={loteOptions.find((o) => o.label === filters.lote) || null}
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  lote: value?.label ?? "",
                  oc: "",
                  fechaVencimiento: null,
                  fechaIngreso: null,
                }))
              }
              disabled={
                !filters.amountPerContainer || loadingMovements || loadingFilterOptions
              }
              renderInput={(params) => <TextField {...params} label="Lote" />}
            />
          </Grid>

          <Grid item md={2.4} sm={6} xs={12}>
            <Autocomplete
              options={ocOptions}
              value={ocOptions.find((o) => o.label === filters.oc) || null}
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  oc: value?.label ?? "",
                  fechaVencimiento: null,
                  fechaIngreso: null,
                }))
              }
              disabled={!filters.lote || loadingMovements || loadingFilterOptions}
              renderInput={(params) => <TextField {...params} label="OC" />}
            />
          </Grid>

          <Grid item md={2.4} sm={6} xs={12}>
            <Autocomplete
              options={fechaVencimientoOptions}
              value={
                fechaVencimientoOptions.find((o) =>
                  filters.fechaVencimiento
                    ? dayjs(o.value).isSame(filters.fechaVencimiento, "day")
                    : false,
                ) || null
              }
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  fechaVencimiento: value?.value ? new Date(value.value) : null,
                  fechaIngreso: null,
                }))
              }
              disabled={!filters.oc || loadingMovements || loadingFilterOptions}
              renderInput={(params) => (
                <TextField {...params} label="Fecha Vencimiento" />
              )}
            />
          </Grid>

          <Grid item md={2.4} sm={6} xs={12}>
            <Autocomplete
              options={fechaIngresoOptions}
              value={
                fechaIngresoOptions.find((o) =>
                  filters.fechaIngreso
                    ? dayjs(o.value).isSame(filters.fechaIngreso, "day")
                    : false,
                ) || null
              }
              onChange={(_, value) =>
                setFilters((prev) => ({
                  ...prev,
                  fechaIngreso: value?.value ? new Date(value.value) : null,
                }))
              }
              disabled={
                !filters.fechaVencimiento ||
                loadingMovements ||
                loadingFilterOptions
              }
              renderInput={(params) => (
                <TextField {...params} label="Fecha Ingreso" />
              )}
            />
          </Grid>

          <Grid item md={2.4} sm={6} xs={12}>
            <TextField
              label="Descripción de Artículo"
              type="text"
              fullWidth
              defaultValue={filters.articleDescription ?? ""}
              onBlur={(e) =>
                updateFilters({
                  articleDescription: e.target.value.trim() || undefined,
                  amountPerContainer: undefined,
                  lote: "",
                  oc: "",
                  fechaVencimiento: null,
                  fechaIngreso: null,
                })
              }
              disabled={loadingMovements || !isClientSelected}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel id="operation-type-label">
                Tipo de movimiento
              </FormLabel>
              <FormGroup row>
                {MOVEMENT_TYPE_OPTIONS.filter(
                  (type) =>
                    !isClientUser ||
                    CLIENT_MOVEMENT_TYPE_IDS.includes(type.value),
                ).map((type) => (
                  <FormControlLabel
                    key={type.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.movementTypeIds.includes(type.value)}
                        onChange={(_, checked) => {
                          const next = checked
                            ? [...filters.movementTypeIds, type.value]
                            : filters.movementTypeIds.filter(
                                (id) => id !== type.value,
                              );
                          updateFilters({ movementTypeIds: next });
                        }}
                      />
                    }
                    label={type.label}
                    sx={{
                      ".MuiFormControlLabel-label": { fontSize: "0.8rem" },
                    }}
                  />
                ))}

                {!isClientUser && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={filters.movementTypeIds.length === 0}
                        onChange={() => updateFilters({ movementTypeIds: [] })}
                      />
                    }
                    label="Todos"
                    sx={{ ".MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                  />
                )}
              </FormGroup>
            </FormControl>
          </Grid>

          {!isClientUser && (
            <Grid item xs={4}>
              <FormControl>
                <FormLabel id="operation-type-label">Borradores</FormLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={loadingMovements}
                      checked={filters.includeDrafts ?? false}
                      size="small"
                      onChange={(_, checked) =>
                        updateFilters({ includeDrafts: checked })
                      }
                    />
                  }
                  label="Incluir borradores"
                  sx={{ ".MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                />
              </FormControl>
            </Grid>
          )}

          {!isClientUser && (
            <Grid item xs={4}>
              <FormControl>
                <FormLabel id="operation-type-label">Ajustes</FormLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      disabled={loadingMovements}
                      checked={filters.includeAdjustments ?? false}
                      size="small"
                      onChange={(_, checked) =>
                        updateFilters({ includeAdjustments: checked })
                      }
                    />
                  }
                  label="Incluir ajustes de inventario"
                  sx={{ ".MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                />
              </FormControl>
            </Grid>
          )}

          <Grid
            item
            container
            xs={12}
            md={4}
            ml="auto"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            <Button
              variant="contained"
              color="success"
              startIcon={
                loadingAllMovements ? (
                  <CircularProgress color="inherit" size={16} />
                ) : (
                  <FaFileDownload />
                )
              }
              onClick={() => handleExportMovements("excel")}
              disabled={loadingAllMovements}
            >
              {loadingAllMovements ? "Exportando..." : "Exportar Excel"}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={
                loadingAllMovements ? (
                  <CircularProgress color="inherit" size={16} />
                ) : (
                  <FaFileDownload />
                )
              }
              onClick={() => handleExportMovements("pdf")}
              disabled={loadingAllMovements}
              sx={{ ml: 1 }}
            >
              {loadingAllMovements ? "Exportando..." : "Exportar PDF"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container my={2}>
        {loadingMovements ? (
          <Grid item xs={12} display="flex" justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : filters.clientId === undefined || filters.clientId === null ? (
          <Grid item xs={12}>
            <Alert
              severity="info"
              text={
                isClientUser
                  ? "El usuario cliente no tiene un cliente asociado"
                  : "Seleccione un cliente para visualizar sus movimientos"
              }
            />
          </Grid>
        ) : movements.length === 0 ? (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              text="No hay resultados para los filtros seleccionados"
            />
          </Grid>
        ) : (
          <Grid container gap={4}>
            <Grid item xs={12}>
              <OperationsLogs movements={movements} client={selectedClient} />
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <PaginationBar
                pageSize={10}
                currentPage={filters.page}
                totalCount={totalCount}
                isDisabled={loadingMovements || movements.length === 0}
                onPageChange={setPage}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </PageLayout>
  );
}
